import { Play, Pause } from "lucide-react";
import { useVideoPlayback } from "@/hooks/useVideoPlayback";
import { API } from "@/lib/api";

const TRANSITION_CLASS = {
  none: "",
  fade: "tr-fade",
  slide: "tr-slide",
  zoom: "tr-zoom",
  dissolve: "tr-dissolve",
};

/**
 * Slideshow video preview: plays voiceover audio while cycling scene images
 * with the chosen per-scene transition, ken-burns effect and on-screen captions.
 */
export default function VideoPlayer({ scenes = [], audioUrl, title, videoId, mediaToken }) {
  const total = scenes.length || 1;
  const { playing, active, audioRef, start, stop } = useVideoPlayback(total, audioUrl);
  const current = scenes[active] || {};
  const trClass = playing ? TRANSITION_CLASS[current.transition || "fade"] || "" : "";
  const imgSrc = current.has_image && current.sid && mediaToken
    ? `${API}/videos/${videoId}/scene-image/${current.sid}?token=${mediaToken}&v=${encodeURIComponent(current.image_updated_at || "")}`
    : null;

  return (
    <div className="border border-white/10 rounded-md overflow-hidden bg-black">
      <div
        className="relative aspect-video bg-[#0A0A0A] flex items-center justify-center overflow-hidden"
        data-testid="video-player"
      >
        <div key={active} className={`absolute inset-0 ${trClass}`} data-testid="player-scene-frame">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover ${playing ? "ken-burns" : ""}`}
            />
          ) : (
            <div className="absolute inset-0 grid-noise bg-gradient-to-br from-[#121212] to-[#0A0A0A]" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30" />

        <div className="relative z-10 px-8 text-center max-w-3xl">
          {current.on_screen_text && (
            <p
              className="font-head text-2xl md:text-4xl font-black tracking-tight drop-shadow-lg"
              data-testid="player-caption"
            >
              {current.on_screen_text}
            </p>
          )}
        </div>

        {!playing && (
          <button
            onClick={start}
            data-testid="player-play-btn"
            className="absolute z-20 h-16 w-16 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-all hover:scale-110 pulse-ring"
          >
            <Play className="h-7 w-7 text-white ml-1" fill="white" />
          </button>
        )}
        {playing && (
          <button
            onClick={stop}
            data-testid="player-pause-btn"
            className="absolute bottom-4 left-4 z-20 h-10 w-10 rounded-full bg-black/60 backdrop-blur hover:bg-black/80 flex items-center justify-center transition-colors"
          >
            <Pause className="h-4 w-4 text-white" fill="white" />
          </button>
        )}

        <div className="absolute bottom-4 right-4 z-20 flex gap-1.5">
          {scenes.map((s, i) => (
            <span
              key={s.sid || `pip-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-blue-500" : "w-1.5 bg-white/30"}`}
            />
          ))}
        </div>
      </div>
      {audioUrl && <audio ref={audioRef} src={audioUrl} onEnded={stop} className="hidden" />}
      <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between">
        <span className="text-xs text-zinc-500 truncate">{title}</span>
        <span className="text-xs text-zinc-500">
          Scene {active + 1}/{total}
        </span>
      </div>
    </div>
  );
}
